import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/shared/lib/supabase';
import type { FutebolBolaoPalpite } from '@/shared/types';
import { futebolRepository } from '../../data/futebolRepository';

function mesclarPalpite(
  prev: FutebolBolaoPalpite[],
  novo: FutebolBolaoPalpite,
  fixtureId: number,
): FutebolBolaoPalpite[] {
  if (novo.fixture_id !== fixtureId) return prev;
  if (prev.some((p) => p.id === novo.id)) return prev;
  const semOtimista = prev.filter((p) =>
    !(p.id !== novo.id && (
      (p.session_id && p.session_id === novo.session_id) ||
      p.identificacao === novo.identificacao
    )),
  );
  return [...semOtimista, novo].sort((a, b) =>
    a.identificacao.localeCompare(b.identificacao),
  );
}

/**
 * Lista ao vivo de palpites do bolão (fetch + CDC INSERT + catch-up no resume).
 * Canal longo: cbRef + removeChannel + visibilitychange dentro do hook.
 */
export function useBolaoAoVivo(telaId: string, fixtureId: number): {
  palpites: FutebolBolaoPalpite[];
  adicionarPalpite: (novo: FutebolBolaoPalpite) => void;
  erroLista: string | null;
} {
  const [palpites, setPalpites] = useState<FutebolBolaoPalpite[]>([]);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const geracaoRef = useRef(0);

  const cbRef = useRef({ fixtureId });
  useEffect(() => { cbRef.current = { fixtureId }; });

  const adicionarPalpite = useCallback((novo: FutebolBolaoPalpite) => {
    setPalpites((prev) => mesclarPalpite(prev, novo, cbRef.current.fixtureId));
  }, []);

  useEffect(() => {
    if (!fixtureId || !telaId) {
      setPalpites([]);
      setErroLista(null);
      return;
    }

    setPalpites([]);
    setErroLista(null);

    const carregarPalpites = () => {
      const geracao = ++geracaoRef.current;
      futebolRepository.buscarTodosPalpites(telaId, fixtureId)
        .then(lista => {
          if (geracao !== geracaoRef.current) return;
          setPalpites(lista);
          setErroLista(null);
        })
        .catch(() => {
          if (geracao !== geracaoRef.current) return;
          setErroLista('Não foi possível carregar os palpites.');
        });
    };

    let canal = supabase
      .channel(`bolao-${telaId}-${fixtureId}`)
      .on(
        'postgres_changes' as never,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'futebol_bolao_palpites',
          filter: `tela_id=eq.${telaId}`,
        } as never,
        (payload: { new: FutebolBolaoPalpite }) => {
          const fid = cbRef.current.fixtureId;
          setPalpites((prev) => mesclarPalpite(prev, payload.new, fid));
        },
      )
      .subscribe();

    const criarCanal = () => {
      canal = supabase
        .channel(`bolao-${telaId}-${fixtureId}`)
        .on(
          'postgres_changes' as never,
          {
            event: 'INSERT',
            schema: 'public',
            table: 'futebol_bolao_palpites',
            filter: `tela_id=eq.${telaId}`,
          } as never,
          (payload: { new: FutebolBolaoPalpite }) => {
            const fid = cbRef.current.fixtureId;
            setPalpites((prev) => mesclarPalpite(prev, payload.new, fid));
          },
        )
        .subscribe();
    };

    const destruirCanal = () => {
      supabase.removeChannel(canal);
    };

    carregarPalpites();

    const aoVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        destruirCanal();
      } else {
        destruirCanal();
        carregarPalpites();
        criarCanal();
      }
    };

    document.addEventListener('visibilitychange', aoVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', aoVisibilityChange);
      geracaoRef.current += 1;
      destruirCanal();
    };
  }, [telaId, fixtureId]);

  return { palpites, adicionarPalpite, erroLista };
}
